import React, { useRef, useState } from 'react';
import CalendarPopover from './CalendarPopover';
import './CalendarSelector.css';

let selectorId = 0;

function CalendarSelector({
    label,
    options,
    selectedValues,
    onSelect,
    multiple = false,
    closeOnSelect,
    emptyLabel = 'Selecciona',
}) {
    const [open, setOpen] = useState(false);
    const selectorRef = useRef(null);
    const menuIdRef = useRef(null);

    if (menuIdRef.current === null) {
        selectorId += 1;
        menuIdRef.current = `calendar-selector-menu-${selectorId}`;
    }

    const selected = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
    const selectedOptions = options.filter(option => selected.includes(option.value));
    const selectedLabel = selectedOptions.length > 0
        ? selectedOptions.map(option => option.label).join(', ')
        : emptyLabel;
    const shouldCloseOnSelect = closeOnSelect ?? !multiple;

    const handleSelect = value => {
        onSelect(value);
        if (shouldCloseOnSelect) {
            setOpen(false);
        }
    };

    const handleKeyDown = event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
        } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen(prev => !prev);
        }

        if (event.key === 'Escape') {
            setOpen(false);
        }
    };

    const handleMenuKeyDown = event => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

        event.preventDefault();
        const items = Array.from(event.currentTarget.querySelectorAll('[role="option"]'));
        const current = items.indexOf(document.activeElement);
        let next = event.key === 'ArrowUp' ? current - 1 : current + 1;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = items.length - 1;
        items[(next + items.length) % items.length]?.focus();
    };

    return (
        <div className={`calendar-custom-selector${open ? ' open' : ''}`} ref={selectorRef}>
            <div
                className={`calendar-custom-selector-trigger${open ? ' open' : ''}`}
                role="combobox"
                aria-label={label}
                aria-expanded={open}
                aria-haspopup="listbox"
                aria-controls={menuIdRef.current}
                tabIndex={0}
                onClick={() => setOpen(prev => !prev)}
                onKeyDown={handleKeyDown}
            >
                <span className="calendar-custom-selector-value" title={selectedLabel}>{selectedLabel}</span>
                <span className="calendar-custom-selector-chevron">▾</span>
            </div>

            {open ? (
                <CalendarPopover
                    anchorRef={selectorRef}
                    onClose={() => setOpen(false)}
                    initialFocus="[role='option']"
                    onKeyDown={handleMenuKeyDown}
                    id={menuIdRef.current}
                    className="calendar-custom-selector-menu"
                    role="listbox"
                    aria-label={label}
                    aria-multiselectable={multiple || undefined}
                >
                    {options.map(option => {
                        const isSelected = selected.includes(option.value);

                        return (
                            <div
                                key={String(option.value)}
                                className={`calendar-custom-selector-option${isSelected ? ' selected' : ''}`}
                                role="option"
                                aria-selected={isSelected}
                                tabIndex={0}
                                onClick={() => handleSelect(option.value)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        handleSelect(option.value);
                                    }
                                }}
                            >
                                <span className="calendar-custom-selector-check">{isSelected ? '✓' : ''}</span>
                                <span>{option.label}</span>
                            </div>
                        );
                    })}
                </CalendarPopover>
            ) : null}
        </div>
    );
}

export default CalendarSelector;
